import { useState, useEffect, useRef } from "react";
import Select from "react-select";
import axios from "../api/axios";
import { Rating } from "react-simple-star-rating";
import moment from "moment";
import jwt_decode from "jwt-decode";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { SERVER_URL } from "../ServerLink";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Form,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  Badge,
  Table,
  Card,
  CardTitle,
  CardBody,
  Col,
  Row,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from "reactstrap";
import { toast } from "react-toastify";
import classnames from "classnames";
import { currencyFormat } from "../../helpers/helper";
import { ConsumedInventoryItems } from "../Inventory/pages/ConsumeInventoryItems/ConsumedInventoryItems";
import ConsumedInventoryItemsPreview from "../Inventory/pages/ConsumeInventoryItems/ConsumedInventoryItemsPreview";
import { TicketHistoryTimeline } from "./TicketHistoryTimeline";
import { MaintenanceFlowDetailsForm } from "../TechnicalStaff/MaintenanceFlowDetailsForm";
import { requestTypeLabel } from "../../helpers/requestTypeLabel";
import { ticketAge } from "./ticketAge";

// ─────────────────────────────────────────────────────────────
// WorkflowStatusCell — renders inline workflow details in table
// ─────────────────────────────────────────────────────────────
const WorkflowStatusCell = ({ status, workflow }) => {
  const cellStyle = {
    minWidth: 200,
    fontSize: "0.78rem",
    verticalAlign: "middle",
    padding: "6px 8px",
    lineHeight: 1.6,
  };

  const row = (label, value) =>
    value ? (
      <div key={label}>
        <span style={{ fontWeight: 500 }}>{label}:</span> {value}
      </div>
    ) : null;

  const fmtDate = (d, includeYear = true) => {
    if (!d) return null;

    const date = new Date(d);

    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", {
      month: "short",
      timeZone: "UTC",
    });
    const year = date.getUTCFullYear();

    return includeYear ? `${day} ${month} ${year}` : `${day} ${month}`;
  };

  if (!workflow) {
    return (
      <td style={cellStyle}>
        <span style={{ color: "var(--bs-secondary)", fontSize: "0.75rem" }}>—</span>
      </td>
    );
  }

  const { scheduling, progress, closure, history, residentFeedbackConfirmed, updatedAt } = workflow;

  if (status === "Scheduled") {
    return (
      <td style={cellStyle}>
        <div style={{ fontWeight: 600, marginBottom: 3 }}>📅 Scheduled</div>
        {row("Date", fmtDate(scheduling?.date))}
        {row(
          "Time",
          scheduling?.timeSlot?.start
            ? `${scheduling.timeSlot.start} – ${scheduling.timeSlot.end}`
            : null
        )}
        {row("Tech", scheduling?.technicianName)}
        {row("Phone", scheduling?.technicianPhone)}
        {!scheduling?.date && !scheduling?.technicianName && (
          <span style={{ color: "var(--bs-secondary)" }}>Not yet scheduled</span>
        )}
      </td>
    );
  }

  if (status === "Inprogress") {
    return (
      <td style={cellStyle}>
        <div style={{ fontWeight: 600, marginBottom: 3 }}>🔧 In Progress</div>
        {row("Notes", progress?.notes)}
        {row("Parts", progress?.partsOrdered)}
        {row("ETA", fmtDate(progress?.expectedArrivalDate, false))}
        {row("Follow-up", fmtDate(progress?.followUpDate, false))}
        {!progress?.notes && !progress?.partsOrdered && !progress?.expectedArrivalDate && (
          <span style={{ color: "var(--bs-secondary)" }}>No details yet</span>
        )}
      </td>
    );
  }

  if (status === "Completed") {
    const closedByName = closure?.closedBy?.userId
      ? `${closure.closedBy.userId.first_name || ""} ${closure.closedBy.userId.last_name || ""}`.trim()
      : null;

    const closedDate =
      fmtDate(closure?.closedAt) || fmtDate(updatedAt);

    return (
      <td style={cellStyle}>
        <div style={{ fontWeight: 600, marginBottom: 3 }}>✅ Completed</div>
        {row("Closed", closedDate)}
        {row("Notes", closure?.notes)}
        {row("By", closedByName)}
        <div>
          <span style={{ fontWeight: 500 }}>Feedback:</span>{" "}
          {residentFeedbackConfirmed ? (
            <span style={{ color: "var(--bs-success)" }}>✓ Confirmed</span>
          ) : (
            <span style={{ color: "var(--bs-warning)" }}>✗ Pending</span>
          )}
        </div>
      </td>
    );
  }

  if (status === "Unresolved") {
    const historyArr = history || [];
    const lastEntry = historyArr[historyArr.length - 1];
    return (
      <td style={cellStyle}>
        <div style={{ fontWeight: 600, marginBottom: 3 }}>⚠️ Unresolved</div>
        {row("Reason", closure?.notes)}
        {lastEntry?.at && row("Last update", fmtDate(lastEntry.at))}
        {!closure?.notes && !lastEntry && (
          <span style={{ color: "var(--bs-secondary)" }}>No details recorded</span>
        )}
      </td>
    );
  }

  // Open / default
  return (
    <td style={cellStyle}>
      <div style={{ fontWeight: 600, marginBottom: 3 }}>📋 Open</div>
      <span style={{ color: "var(--bs-secondary)", fontSize: "0.75rem" }}>
        Awaiting assignment
      </span>
    </td>
  );
};

// ─────────────────────────────────────────────────────────────
// StatusHoverContent — used in OverlayTrigger tooltip (unchanged)
// ─────────────────────────────────────────────────────────────
const StatusHoverContent = ({ status, hoverData }) => {
  if (hoverData === "loading") {
    return (
      <div style={{ textAlign: "center", padding: "4px", minWidth: "160px" }}>
        <span className="spinner-border spinner-border-sm me-1" /> Loading...
      </div>
    );
  }

  if (!hoverData || hoverData === "error") {
    return (
      <div style={{ fontSize: "0.78rem", minWidth: "160px" }}>
        <p className="mb-0 text-muted">No data available.</p>
      </div>
    );
  }

  const workflow = hoverData?.workflow;
  const scheduling = workflow?.scheduling;
  const progress = workflow?.progress;
  const closure = workflow?.closure;

  if (status === "Scheduled") {
    return (
      <div style={{ textAlign: "left", fontSize: "0.78rem", minWidth: "200px" }}>
        <p className="mb-1"><strong>📅 Scheduled Visit</strong></p>
        <p className="mb-1">Date: {scheduling?.date ? new Date(scheduling.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "Not set"}</p>
        <p className="mb-1">Time: {scheduling?.timeSlot ? `${scheduling.timeSlot.start} – ${scheduling.timeSlot.end}` : "Not set"}</p>
        <p className="mb-1">Technician: {scheduling?.technicianName || "Not assigned"}</p>
        <p className="mb-0">Phone: {scheduling?.technicianPhone || "N/A"}</p>
      </div>
    );
  }

  if (status === "Inprogress") {
    return (
      <div style={{ textAlign: "left", fontSize: "0.78rem", minWidth: "200px" }}>
        <p className="mb-1"><strong>🔧 In Progress</strong></p>
        <p className="mb-1">Notes: {progress?.notes || "None"}</p>
        <p className="mb-1">Parts Ordered: {progress?.partsOrdered || "None"}</p>
        <p className="mb-1">Expected Arrival: {progress?.expectedArrivalDate ? new Date(progress.expectedArrivalDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "Not set"}</p>
        <p className="mb-0">Follow-up: {progress?.followUpDate ? new Date(progress.followUpDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "Not set"}</p>
      </div>
    );
  }

  if (status === "Completed") {
    return (
      <div style={{ textAlign: "left", fontSize: "0.78rem", minWidth: "200px" }}>
        <p className="mb-1"><strong>✅ Completed</strong></p>
        <p className="mb-1">Closed At: {closure?.closedAt ? new Date(closure.closedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : workflow?.updatedAt ? new Date(workflow.updatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "Not set"}</p>
        <p className="mb-1">Notes: {closure?.notes || "N/A"}</p>
        <p className="mb-1">Closed By: {closure?.closedBy ? `${closure.closedBy.first_name || ""} ${closure.closedBy.last_name || ""}`.trim() : "N/A"}</p>
        <p className="mb-0">Feedback Confirmed: {workflow?.residentFeedbackConfirmed ? "✓ Yes" : "✗ No"}</p>
      </div>
    );
  }

  if (status === "Unresolved") {
    const historyArr = workflow?.history || [];
    const lastEntry = historyArr[historyArr.length - 1];
    return (
      <div style={{ textAlign: "left", fontSize: "0.78rem", minWidth: "200px" }}>
        <p className="mb-1"><strong>⚠️ Unresolved</strong></p>
        <p className="mb-1">Reason: {closure?.notes || "Not specified"}</p>
        <p className="mb-0">Last Updated: {lastEntry?.at ? new Date(lastEntry.at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "Not set"}</p>
      </div>
    );
  }

  const firstTicketHistory = hoverData?.ticketHistory?.[0];
  return (
    <div style={{ textAlign: "left", fontSize: "0.78rem", minWidth: "200px" }}>
      <p className="mb-1"><strong>📋 Open</strong></p>
      <p className="mb-1">ID: {hoverData?.primaryID || "N/A"}</p>
      <p className="mb-0">Submitted: {firstTicketHistory?.createdAt ? new Date(firstTicketHistory.createdAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "N/A"}</p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Main TicketRow component
// ─────────────────────────────────────────────────────────────
export default function TicketRow(props) {
  const [statusHoverData, setStatusHoverData] = useState(null);
  const decode = jwt_decode(window.localStorage.getItem("accessToken"));
  const [modal_view, setmodal_view] = useState(false);
  const [modal_workflow, setmodal_workflow] = useState(false);
  const [workflowTargetStatus, setWorkflowTargetStatus] = useState("");
  const [inventoryItemConsumedDetails, setInventoryItemConsumedDetails] = useState(null);
  const [ratingModel, setRatingModel] = useState(false);
  const [review, setReview] = useState("");
  const [ratings, setRating] = useState("");
  const [model_open, setModel_open] = useState(false);
  const [customActiveTab, setcustomActiveTab] = useState(
    props.isConfirmationSet ? "2" : "1"
  );
  const [showModal, setShowModal] = useState(false);

  const openModal = () => {
    getNotes();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    props.searchTicket();
  };

  const [checked, setchecked] = useState(false);
  const [assignedVendor, setAssignedVendor] = useState(
    props?.assignedTo
      ? props.assignedTo?.first_name
      : props.assignSpecificVendors?.[0]?.first_name
  );
  const [assignedRole, setAssignedRole] = useState(
    props?.assignedTo
      ? props.assignedTo?.role
      : props.assignSpecificVendors?.[0]?.role
  );

  const [status, setStatus] = useState(props.status ? props.status : "select");
  const [startDate, setStartDate] = useState(
    props.startDate ? props.startDate.split("T")[0] : Date.now()
  );
  const [startTime, setStartTime] = useState(
    props.startTime ? props.startTime : ""
  );
  const [endDate, setEndDate] = useState(
    props.estimatedEndDate ? props.estimatedEndDate.split("T")[0] : Date.now()
  );
  const [endTime, setEndTime] = useState(props.endTime ? props.endTime : "");
  const [notes, setNotes] = useState(props.notes ? props.notes : "");
  const [moveTo, setMoveTo] = useState("");
  const [vendor, setVendor] = useState([]);
  const [vendorError, setVendorError] = useState("");
  const [modal_large, setmodal_large] = useState(false);
  const [modal_large2, setmodal_large2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableCheck, setTableCheck] = useState(false);
  const [tableContent, setTableContent] = useState("");

  const [technicalStaff, setTechnicalStaff] = useState([]);
  const [originalStaff, setOriginalStaff] = useState([]);
  const [vendorStaff, setVendorStaff] = useState([]);
  const [specialties, setSpecialities] = useState([]);

  const [selectedName, setSelectedName] = useState([]);
  const [selectedRating, setSelectedRating] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState([]);
  const [selectedContact, setSelectedContact] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [inventoryItemsAvailableToBeConsumed, setInventoryItemsAvailableToBeConsumed] = useState([]);

  const [showNote, setShowNote] = useState(false);

  const [quoteModal, setQuoteModal] = useState(false);
  const [assignLoader, setAssignLoader] = useState(false);
  const [Confirm_model, setConfirm_model] = useState(false);
  const [ticketID, setTicketID] = useState("");
  const [ticketStatus, setStatusofTicket] = useState("");
  const [notesInput, setNotesInput] = useState("");
  const [showlogs, setShowLogs] = useState({
    data: [],
    value: false,
  });
  const [showNotes, setShowNotes] = useState([]);

  const [checkboxes, setCheckboxes] = useState({ yes: false, no: false });

  const [timeTaken, setTimeTaken] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState({ value: 1, label: "1" });
  const [commentForDetailsByConsumer, setCommentForDetailsByConsumer] = useState("");
  const [comment, setComment] = useState(null);

  const handleTimeChange = (selectedOption) => {
    setTimeTaken(selectedOption.value);
  };

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  function removeBodyCss() {
    document.body.classList.add("no_padding");
  }

  function tog_view() {
    setmodal_view(!modal_view);
    removeBodyCss();
  }

  function tog_rating_view() {
    setRatingModel(!ratingModel);
  }

  function tog_open_view() {
    setModel_open(!model_open);
  }

  const tog_workflow = () => {
    setmodal_workflow(!modal_workflow);
  };

  const toggleCustom = (tab) => {
    if (customActiveTab !== tab) {
      setcustomActiveTab(tab);
    }
  };

  const PDF_DOWNLOAD_URL = "/ticket/view_ticket_documents";
  const UPDATE_TICKET = "ticket/update_ticket";
  const REASIGN_TICKET = "ticket/reasign";
  const CONFIRM_TICKET = "ticket/confirm_ticket";
  const RATING_TICKET = "ticket/update_rating";
  const ADD_VENDOR = "/vendor/get_vendors";
  const CHANGE_STATUS = "/ticket/update_status";
  const PAY_URL = "/payment/vendor_pay";
  const NOTES = "ticket/add_notes";
  const GET_NOTES = "ticket/get_notes";
  const GET_INVENTORY_ITEMS_BY_REQUEST_TYPE = "v2/inventory/items/request-types";
  const CONSUME_INVENTORY_ITEMS_BY_MAINTENANCE_TICKET = "v2/inventory/:id/consume";
  const GET_CONSUMED_INVENTORY_ITEMS_BY_MAINTENANCE_TICKET = `v2/inventory/consumed/ticket/:ticket_id`;

  const getAvailableInventoryItems = async (params) => {
    const response = await axios.get(GET_INVENTORY_ITEMS_BY_REQUEST_TYPE, {
      params: params,
    });
    setInventoryItemsAvailableToBeConsumed(response?.data?.data?.items || []);
  };

  async function consumeInventoryItems(ticket_id, itemId, quantity, comment) {
    if (!ticket_id || !itemId || !quantity) {
      console.warn("ticket_id, itemId, and quantity are required to consume inventory.");
      return;
    }
    try {
      const response = await axios.post(
        `v2/inventory/${ticket_id}/consume`,
        { itemId, quantity, comment }
      );
      return response;
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(`Failed to consume inventory: ${error.response?.data?.message || error.message}`);
      }
      throw new Error(`Failed to consume inventory: ${error.message}`);
    }
  }

  const fetchConsumedItemsDetails = async ({ _id }) => {
    if (!_id) {
      setInventoryItemConsumedDetails(null);
      return;
    }
    try {
      const apiUrl = GET_CONSUMED_INVENTORY_ITEMS_BY_MAINTENANCE_TICKET.replace(":ticket_id", _id);
      const response = await axios.get(apiUrl);
      if (response.status >= 200 && response.status < 300 && response.data.success) {
        setInventoryItemConsumedDetails(response.data.data);
      } else {
        setInventoryItemConsumedDetails(null);
      }
    } catch (error) {
      console.error("Error fetching consumed items:", error);
      setInventoryItemConsumedDetails(null);
    }
  };

  const fetchStatusHoverData = async () => {
    if (statusHoverData) return;
    setStatusHoverData("loading");
    try {
      const response = await axios.get(`v2/maintenance/workflow/${props.id}/history`);
      if (response.data?.success) {
        setStatusHoverData(response.data.data);
      } else {
        setStatusHoverData("error");
      }
    } catch (e) {
      setStatusHoverData("error");
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    var submited = false;
    try {
      const body = {
        ticketID: props.id,
        customerSatisfaction: checkboxes.yes ? true : false,
        ratings: ratings !== "" ? ratings : 0,
        reviews: review,
      };
      const response = await axios.post(RATING_TICKET, body);
      if (response.data.status === 200) {
        submited = true;
        setReview("");
        setRating("");
        setCheckboxes({ yes: false, no: false });
        props.searchTicket();
        toast("Review Submitted!");
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (submited) {
        tog_rating_view();
        return (
          <div class="alert alert-primary" role="alert">
            This is a primary alert—check it out!
          </div>
        );
      }
    }
  };

  const sendNotes = async (ticketId) => {
    if (notesInput.length) {
      try {
        let response;
        if (decode.role === "manager") {
          const payload = { user: "manager", id: decode.id, notes: notesInput, ticketId, name: decode.firstname };
          response = await axios.post(NOTES, payload);
        } else if (decode.role === "company") {
          const payload = { user: "company", id: decode.id, notes: notesInput, ticketId, name: decode.firstname };
          response = await axios.post(NOTES, payload);
        } else if (decode.role === "customer") {
          const payload = { user: "customer", id: decode.id, notes: notesInput, ticketId, name: decode.firstname };
          response = await axios.post(NOTES, payload);
        }
        if (response.status === 200) {
          setNotesInput("");
          getNotes();
        }
      } catch { }
    } else {
      return;
    }
  };

  const getNotes = async () => {
    const payload = { id: decode.id, user: decode.role, ticketId: props.id };
    const response = await axios.post(GET_NOTES, payload);
    if (response.data.status === 200) {
      setShowNotes(response.data.notes);
    }
  };

  const getVendor = async (e) => {
    try {
      let response;
      if (decode.role === "manager") {
        const companyId = decode?.companyId || decode?.company_id;
        response = await axios.get(`${ADD_VENDOR}/${companyId}`);
      } else if (decode.role === "company") {
        response = await axios.get(ADD_VENDOR + "/" + decode.id);
      } else response = await axios.get(ADD_VENDOR);

      if (response.data?.success) {
        setVendor(response?.data?.vendors);
        response?.data?.vendors?.map((vendorItem, index) => {
          setSpecialities((prevSpecialities) => {
            const newSpecialitiesSet = new Set(prevSpecialities);
            newSpecialitiesSet.add(vendorItem.specialties[0]?.specialty);
            return Array.from(newSpecialitiesSet);
          });
        });
      } else {
        setVendorError(response.data?.errorMessage);
      }
    } catch (error) {
      setVendorError("Something went wrong");
    }
  };

  const payVendor = async (ticketID, vendorID) => {
    try {
      const response = await axios.post(PAY_URL, {
        vendorID: vendorID,
        ticketID: ticketID,
        managerId: decode.id,
      });
      if (response.data.status == 200) {
        window.location.href = response.data.checkout.url;
      }
    } catch (error) {
      console.error(error);
    }
  };

  function tog_large() {
    setmodal_large(!modal_large);
    removeBodyCss();
  }

  const [col5, setcol5] = useState(false);
  const t_col5 = () => {
    setcol5(!col5);
  };

  function tog_large2() {
    setmodal_large2(!modal_large2);
    removeBodyCss();
  }

  const getStatusConfig = (status) => {
    switch (status) {
      case "Completed":
        return { color: "success", icon: "fas fa-check-circle", bgClass: "bg-success", textClass: "text-success" };
      case "Unresolved":
        return { color: "danger", icon: "fas fa-exclamation-circle", bgClass: "bg-danger", textClass: "text-danger" };
      case "Inprogress":
        return { color: "warning", icon: "fas fa-clock", bgClass: "bg-warning", textClass: "text-warning" };
      case "Scheduled":
        return { color: "primary", icon: "fas fa-calendar", bgClass: "bg-primary", textClass: "text-primary" };
      case "Open":
        return { color: "info", icon: "fas fa-folder-open", bgClass: "bg-info", textClass: "text-info" };
      default:
        return { color: "secondary", icon: "fas fa-circle", bgClass: "bg-secondary", textClass: "text-secondary" };
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case "Completed":
        return { type: "success", icon: "fas fa-check-circle", title: "Ready to Complete!", message: "Great work! Please fill in the completion details below." };
      case "Inprogress":
        return { type: "warning", icon: "fas fa-tools", title: "Work in Progress", message: "Task is currently being worked on. Update when ready." };
      case "Unresolved":
        return { type: "danger", icon: "fas fa-exclamation-triangle", title: "Issue Encountered", message: "This task needs attention or has encountered problems." };
      case "Scheduled":
        return { type: "primary", icon: "fas fa-calendar", title: "Schedule a Visit", message: "Set the date, time slot and technician for this visit." };
      case "Open":
        return { type: "info", icon: "fas fa-play-circle", title: "Ready to Start", message: "Task is ready to begin. Update status when you start working." };
      default:
        return { type: "secondary", icon: "fas fa-info-circle", title: "Status Update", message: "Please update the task status accordingly." };
    }
  };

  const submitFunction = async (event) => {
    event.preventDefault();
    var submited = false;
    try {
      const response = await axios.post(UPDATE_TICKET, {
        ticketID: props.id,
        isConfirmationSet: true,
        assignVendor: assignedVendor,
        status: status,
        startDate: startDate,
        endDate: endDate,
        startTime: startTime,
        endTime: endTime,
        notes: notes,
        name: props.name,
        email: props.email,
        property: props.property,
        suite: props.suite,
        companyDomain: props.companyDomain,
      });
      if (response.data.status === 201) {
        submited = true;
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (submited) {
        tog_view();
        return (
          <div class="alert alert-primary" role="alert">
            This is a primary alert—check it out!
          </div>
        );
      }
    }
  };

  const ResignTicket = async () => {
    try {
      const response = await axios.post(REASIGN_TICKET, { ticketID: props.id });
      if (response.data.success) {
        tog_view();
        tog_open_view();
      }
    } catch (e) { }
  };

  const moveToAllVendor = async (id) => {
    setLoading(true);
    try {
      const response = await axios.post("/ticket/move_to_vendor_portal", { ticketID: id });
      if (response.data.sucess) {
        props.getTicket();
      }
      setLoading(false);
      props.getTicket();
    } catch (error) {
      console.error("Unable to fetch", error);
      setLoading(false);
    }
  };

  const moveToSpecificVendor = async (id, staff_id) => {
    setLoading(true);
    try {
      let response;
      if (moveTo == "specific vendors") {
        response = await axios.post("ticket/move_to_specific_vendor", { ticketID: id, vendorID: staff_id });
      } else {
        response = await axios.post("ticket/move_to_technicalStaff", { ticketID: id, assignedStaff: staff_id });
      }
      setLoading(false);
      props.getTicket();
      if (response.data.sucess) {
        props.getTicket();
      }
    } catch (error) {
      console.error("Unable to fetch", error);
      setLoading(false);
    }
  };

  const handleAssign = async (ticketID, vendorID) => {
    try {
      setAssignLoader(true);
      const response = await axios.post("ticket/move_to_specific_vendor", { ticketID, vendorID });
      if (response.data.success) {
        setAssignLoader(false);
      } else {
        setAssignLoader(false);
      }
    } catch (e) {
      setAssignLoader(false);
      console.error("Error: ", e.message);
    }
  };

  const ChangeStatus = async (statusData = null) => {
    setLoading(true);
    try {
      let response, consumedResponse;
      if (statusData) {
        const { status, timeTaken, comment, consumedItems } = statusData;
        response = await axios.post(CHANGE_STATUS, { ticketID: ticketID, status: status, timeTaken, comment });
      } else {
        response = await axios.post(CHANGE_STATUS, { ticketID: ticketID, status: ticketStatus, timeTaken, comment });
        if (selectedItem && selectedQuantity) {
          consumedResponse = await consumeInventoryItems(ticketID, selectedItem.value, selectedQuantity.value, commentForDetailsByConsumer);
        }
      }
      setLoading(false);
      if (response.data.success) {
        props.searchTicket();
        toast.success("Status updated successfully!");
      }
    } catch (error) {
      console.error("Unable to update status:", error);
      toast.error("Failed to update status");
      setLoading(false);
    }
  };

  const getTechnicalStaff = async (url, data) => {
    try {
      const companyId = decode?.companyId || decode?.company_id || decode.id;
      const response = await axios.post(
        `/technicalStaff/get_technical_staff?page=${props.currentPage || 1}&limit=1000`,
        { companyID: companyId }
      );
      if (response.data.technicalStaff) {
        const temp = response.data.technicalStaff;
        setTechnicalStaff(temp);
        setOriginalStaff(temp);
      }
    } catch (e) {
      console.error("Failed to fetch technical staff:", e);
    }
  };

  const technicalStaffSearch = () => {
    let filterList = originalStaff;
    if (selectedName.length > 0) {
      filterList = originalStaff.filter((item) => selectedName.includes(item.first_name) ? item : null);
    }
    if (selectedRating.length > 0) {
      filterList = filterList.filter((item) => selectedRating.includes(item.rating) ? item : null);
    }
    if (selectedEmail.length > 0) {
      filterList = filterList.filter((item) => selectedEmail.includes(item.email) ? item : null);
    }
    if (selectedContact.length > 0) {
      filterList = filterList.filter((item) => selectedContact.includes(item.contact_no) ? item : null);
    }
    setTechnicalStaff(filterList);
  };

  const specificVendorSearch = async () => {
    try {
      const response = await axios.get();
    } catch { }
    let filterList = vendor;
    if (selectedName.length > 0) {
      filterList = vendor.filter((item) => selectedName.includes(item.first_name) ? item : null);
    }
    if (selectedRating.length > 0) {
      filterList = filterList.filter((item) => selectedRating.includes(item.rating) ? item : null);
    }
    if (selectedCategories.length > 0) {
      filterList = filterList.filter((item) => selectedCategories.includes(item.specialties[0].specialty) ? item : null);
    }
    if (selectedContact.length > 0) {
      filterList = filterList.filter((item) => selectedContact.includes(item.contact_no) ? item : null);
    }
    setVendor(filterList);
  };

  const clear = () => {
    setSelectedName([]);
    setSelectedRating([]);
    setSelectedEmail([]);
    setSelectedContact([]);
    setSelectedCategories([]);
    setTechnicalStaff(originalStaff);
    getVendor();
  };

  useEffect(() => {
    getVendor();
    getTechnicalStaff();
    clear();
  }, []);

  let priority = "warning";
  if (props.priority === "High") {
    priority = "danger";
  } else if (props.priority === "Medium") {
    priority = "warning";
  } else if (props.priority === "Low") {
    priority = "info";
  }

  const Confirm_view = async (id, status, ticket_details) => {
    setTicketID(id);
    setStatusofTicket(status);
    setConfirm_model(!Confirm_model);
    await getAvailableInventoryItems({ property_id: ticket_details.ticket.propertyID, building_name: ticket_details.building, request_type: ticket_details.requestType });
  };

  const handlePrint = (data) => {
    const printContent = `
    <div class="print-template">
      <table style="border-collapse: collapse; width: 100%;">
        <tr>
          <td colspan="2" style="text-align: center; padding: 10px; border: 1px solid #ccc;">
            <h2>Ticket Details</h2>
            <p>ID : ${data._id.substring(data._id.length - 6)}</p>
          </td>
        </tr>
        <tr><td style="padding: 10px; border: 1px solid #ccc;">Property Name:</td><td style="padding: 10px; border: 1px solid #ccc;">${data.property}</td></tr>
        <tr><td style="padding: 10px; border: 1px solid #ccc;">Company Name:</td><td style="padding: 10px; border: 1px solid #ccc;">${data.companyDomain}</td></tr>
        <tr><td style="padding: 10px; border: 1px solid #ccc;">Name:</td><td style="padding: 10px; border: 1px solid #ccc;">${data.name}</td></tr>
        <tr><td style="padding: 10px; border: 1px solid #ccc;">Email:</td><td style="padding: 10px; border: 1px solid #ccc;">${data.email}</td></tr>
        <tr><td style="padding: 10px; border: 1px solid #ccc;">Phone:</td><td style="padding: 10px; border: 1px solid #ccc;">${data.phone}</td></tr>
        <tr><td style="padding: 10px; border: 1px solid #ccc;">Suite:</td><td style="padding: 10px; border: 1px solid #ccc;">${data.suite}</td></tr>
        <tr><td style="padding: 10px; border: 1px solid #ccc;">Request Type:</td><td style="padding: 10px; border: 1px solid #ccc;">${requestTypeLabel(data)}</td></tr>
        <tr><td style="padding: 10px; border: 1px solid #ccc;">Details:</td><td style="padding: 10px; border: 1px solid #ccc;">${data.details}</td></tr>
        <tr><td style="padding: 10px; border: 1px solid #ccc;">Status :</td><td style="padding: 10px; border: 1px solid #ccc;">${status}</td></tr>
      </table>
    </div>`;
    const printWindow = window.open("", "maintenance_tickets");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const generateTimeOptions = () => {
    const maxHours = 2;
    const slots = [];
    const maxSlots = (maxHours * 60) / 10 + 1;
    for (let i = 0; i < maxSlots; i++) {
      const totalMinutes = i * 10;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      let timeString = "";
      if (hours > 0) timeString += `${hours} hour${hours > 1 ? "s" : ""}`;
      if (minutes > 0) timeString += ` ${minutes} minute${minutes > 1 ? "s" : ""}`;
      slots.push({ label: timeString.trim() === "" ? "0 minutes" : timeString, value: totalMinutes });
    }
    return slots;
  };

  const generateQuantityOptions = (quantity) => {
    const options = [];
    for (let i = 1; i <= quantity; i++) {
      options.push({ value: i, label: `${i}` });
    }
    return options;
  };

  const itemOptions = inventoryItemsAvailableToBeConsumed.map((item) => ({
    value: item._id,
    label: item.name,
    quantity: item.quantity,
  }));

  const handleItemChange = (option) => {
    setSelectedItem(option);
    setSelectedQuantity(null);
  };

  const handleQuantityChange = (selectedOption) => {
    setSelectedQuantity(selectedOption ? selectedOption : "");
  };

  const handleCommentForDetailsByConsumer = (e) => {
    setCommentForDetailsByConsumer(e.target.value);
  };

  const hasDocuments = props?.documents?.length > 0;

  // Uploaded photos live in S3 (full URLs); very old tickets stored server paths.
  const downloadDocuments = async () => {
    if (!hasDocuments) return;
    if (props.documents[0]?.startsWith("http")) {
      const prefix = "https://rdclive.nyc3.digitaloceanspaces.com/my%20spaces/";
      try {
        const documentNames = props.documents.map((doc) => doc.replace(prefix, ""));
        const documentNewNames = props.documents.map((doc, i) => `${props.suite}_${props.type}_${props.name}_${i + 1}`);
        const response = await axios.post("s3-services/download-images", { paths: documentNames, filenames: documentNewNames });
        const signedUrls = response.data.signedUrls;
        for (let i = 0; i < signedUrls.length; i++) {
          const { signedUrl, customFileName, error } = signedUrls[i];
          if (error) { console.error(error); continue; }
          let link = document.createElement("a");
          link.href = signedUrl;
          link.download = customFileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          await new Promise((resolve) => setTimeout(resolve, 5000));
        }
      } catch (error) {
        console.error("Error fetching signed URLs:", error);
      }
      return;
    }
    const response = await axios.post(PDF_DOWNLOAD_URL, { id: props.id });
    for (let i = 0; i < response.data.documents.length; i++) {
      const url = `${SERVER_URL}${response.data.documents[i]}`;
      const filePath = response.data.documents[i];
      const extension = filePath.substring(filePath.lastIndexOf(".") + 1);
      fetch(url, { method: "GET", headers: {} })
        .then((response) => {
          response.arrayBuffer().then(function (buffer) {
            const url = window.URL.createObjectURL(new Blob([buffer]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `ticket.${extension}`);
            document.body.appendChild(link);
            link.click();
          });
        })
        .catch((err) => { console.error(err); });
    }
  };

  const age = ticketAge({
    status: props.status,
    createdAt: props.createdAt,
    closedAt: props.maintenanceWorkflow?.closure?.closedAt,
  });
  const displayId = props.ticket?.primaryID || props.id.substring(props.id.length - 6);
  const unreadNotes = props.notesCount?.[props.id] || 0;
  const isStaff = decode.role !== "customer";
  const canAssign =
    (decode.role === "company" && !props.isMovedToVendorPortal) ||
    (decode.role === "manager" && !props.isMovedToVendorPortal);
  const assigneeName = (() => {
    const a = props.assignedTo;
    if (!a || typeof a !== "object") return "";
    return [a.first_name ?? a.firstname, a.last_name ?? a.lastname].filter(Boolean).join(" ") || a.agency_name || "";
  })();

  return (
    <tr key={props._key} className={`ticket-row ticket-row--${age.tone}`}>
      <td className="ticket-cell-request">
        <div className="ticket-request-type">{requestTypeLabel(props)}</div>
        <div className="ticket-request-meta">
          <span className="ticket-request-id">{displayId}</span>
          {" · "}
          {props.name ? <>by {props.name} · </> : null}
          {moment(props.createdAt).format("MMM D, YYYY")}
        </div>
        <Modal size="lg" isOpen={modal_view} toggle={() => { tog_view(); }}>
          <div className="modal-header pb-0">
            <h5 className="modal-title mt-0" id="myLargeModalLabel">
              View Ticket list Details
            </h5>
            <button onClick={() => { tog_view(); }} type="button" className="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <Nav tabs className="nav-tabs-custom nav-justified ViewTicketListDetail">
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({ active: customActiveTab === "1" })}
                  onClick={() => { toggleCustom("1"); }}
                >
                  <span className="d-block d-sm-none"><i className="fas fa-home"></i></span>
                  <span className="d-none d-sm-block">Ticket Detail</span>
                </NavLink>
              </NavItem>
              {decode.role != "customer" && (
                <NavItem>
                  <NavLink
                    style={{ cursor: "pointer" }}
                    className={classnames({ active: customActiveTab === "2" })}
                    onClick={() => { toggleCustom("2"); }}
                  >
                    <span className="d-block d-sm-none"><i className="fas fa-home"></i></span>
                    <span className="d-none d-sm-block">Assign</span>
                  </NavLink>
                </NavItem>
              )}
            </Nav>
            <TabContent activeTab={customActiveTab} className="p-3 text-muted">
              <TabPane tabId="1">
                <Form>
                  <div className="row align-items-center">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="formrow-facility-input">Company Name</Label>
                        <Input type="text" value={props.companyDomain} className="form-control" id="formrow-room-input" readOnly />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="formrow-facility-input">Status</Label>
                        <Input type="text" value={props.status} className="form-control" id="formrow-room-input" readOnly />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <Label className="form-Label">Property Name</Label>
                        <Input type="text" value={props.property} className="form-control" id="formrow-room-input" readOnly />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <Label className="form-Label">Suite</Label>
                        <Input type="text" value={props.suite} className="form-control" id="formrow-room-input" readOnly />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="formrow-room-input">requestType</Label>
                        <Input type="text" value={requestTypeLabel(props)} className="form-control" id="formrow-room-input" readOnly />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="formrow-phone-input">Phone</Label>
                        <Input type="text" value={props.phone} className="form-control" id="formrow-room-input" readOnly />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="formrow-email-input">Email</Label>
                        <Input type="text" value={props.email} className="form-control" id="formrow-room-input" readOnly />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="formrow-email-input">Name</Label>
                        <Input type="text" value={props.name} className="form-control" id="formrow-room-input" readOnly />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="formrow-phone-input">Permission for Entering</Label>
                        <Input type="text" value={props.permission} className="form-control" id="formrow-room-input" readOnly />
                      </div>
                    </div>
                    {props.building && (
                      <div className="col-md-4">
                        <div className="mb-3">
                          <Label className="form-label" htmlFor="formrow-phone-input">Building Name</Label>
                          <Input type="text" value={props.building} className="form-control" id="formrow-room-input" readOnly />
                        </div>
                      </div>
                    )}
                    <div className="col-md-4">
                      <div className="mb-3">
                        <Label className="form-Label" htmlFor="formrow-room-input">Assigned To</Label>
                        <Input
                          type="text"
                          value={assignedVendor ? assignedVendor + " ( " + assignedRole + " ) " : "No AssignedTo"}
                          onChange={(e) => { setAssignedVendor(e.target.value); }}
                          className="form-control"
                          id="formrow-room-input"
                          readOnly={true}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="formrow-phone-input">Rating</Label>
                        <div>
                          <td><Rating initialValue={props.rating} readonly={true} size={20} /></td>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label htmlFor="basicpill-address-input" className="form-label">Details</label>
                        <textarea id="basicpill-address-input" className="form-control" rows="3" readOnly placeholder={props.details}></textarea>
                      </div>
                    </div>
                    <Col md={12}>
                      <ConsumedInventoryItemsPreview ticketId={props.id} />
                    </Col>
                    <ImagesDisplay documents={props.documents} />
                    {props.status === "Completed" && (
                      <>
                        <div className="col-md-12">
                          <div className="mb-3">
                            <label htmlFor="basicpill-address-input" className="form-label">Time Taken</label>
                            <input
                              id="basicpill-address-input"
                              className="form-control"
                              type="text"
                              rows="3"
                              readOnly
                              value={props?.ticket?.timeTaken ? `${Math.floor(props.ticket.timeTaken / 60)} hours ${props.ticket.timeTaken % 60} minutes` : "Time taken: Not recorded"}
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="mb-3">
                            <label htmlFor="basicpill-address-input" className="form-label">Comment</label>
                            <textarea id="basicpill-address-input" className="form-control" rows="3" readOnly value={props?.ticket?.comment || "No comments provided"}></textarea>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </Form>
              </TabPane>
              <TabPane tabId="2">
                <div className="row">
                  <div className={`${assignedRole === "vendor" ? "col-md-4" : "col-md-12"}`}>
                    <div className="mb-4">
                      <Label className="form-Label">Assigned To</Label>
                      <Input
                        type="text"
                        value={assignedVendor ? assignedVendor + " ( " + assignedRole + " ) " : ""}
                        onChange={(e) => { setAssignedVendor(e.target.value); }}
                        className="form-control"
                        id="formrow-room-input"
                        readOnly={true}
                      />
                    </div>
                  </div>
                  {assignedRole === "vendor" && (
                    <div className="col-md-4">
                      <div className="mb-3">
                        <Label className="form-Label">Estimated Amount</Label>
                        <Input type="text" value={currencyFormat(Number(props.estimatedAmount))} className="form-control" id="formrow-room-input" readOnly={true} />
                      </div>
                    </div>
                  )}
                  <div className={`${assignedRole === "vendor" ? "col-md-4" : "col-md-3"}`}>
                    <div className="mb-3">
                      <Label className="form-Label">Status</Label>
                      <select value={status} onChange={(e) => { setStatus(e.target.value); }} className="form-select" disabled={true}>
                        <option value={"Select"}>Select</option>
                        <option value={"Open"}>Open</option>
                        <option value={"Scheduled"}>Scheduled</option>
                        <option value={"Inprogress"}>Inprogress</option>
                        <option value={"Completed"}>Completed</option>
                        <option value={"Unresolved"}>Unresolved</option>
                      </select>
                    </div>
                  </div>
                  <div className={`${assignedRole === "vendor" ? "col-md-6" : "col-md-3"}`}>
                    <div className="mb-3">
                      <Label htmlFor="example-date-input" className="form-Label">Start Date</Label>
                      <Input className="form-control" value={startDate} onChange={(e) => { setStartDate(e.target.value); }} type="date" defaultValue="2019-08-19" id="example-date-input" readOnly={true} />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <Label htmlFor="example-time-input" className="form-Label">Start Time</Label>
                      <Input className="form-control" value={startTime} onChange={(e) => { setStartTime(e.target.value); }} type="time" defaultValue="13:45:00" id="example-time-input" readOnly={true} />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <Label htmlFor="example-date-input" className="form-Label">Completion Date</Label>
                      <Input className="form-control" type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); }} defaultValue="2019-08-19" id="example-date-input" readOnly={true} />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <Label htmlFor="example-time-input" className="form-Label">Completion Time</Label>
                      <Input className="form-control" type="time" value={endTime} onChange={(e) => { setEndTime(e.target.value); }} defaultValue="13:45:00" id="example-time-input" readOnly={true} />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <Label className="form-Label">Notes</Label>
                      <textarea id="basicpill-address-input" className="form-control" rows="3" value={notes} onChange={(e) => { setNotes(e.target.value); }} readOnly={true} placeholder=""></textarea>
                    </div>
                  </div>
                  {props.role == "manager" && props.status != "Completed" && (
                    <div className="col-md-12 d-flex flex-wrap align-items-center justify-content-center">
                      <button className="btn btn-primary mo-mb-2 mr-10" onClick={(e) => { ResignTicket(e); }}>Reassign</button>
                      <button className="btn btn-light" onClick={(e) => { tog_view(); }}>Cancel</button>
                    </div>
                  )}
                </div>
              </TabPane>
              <div className="d-flex justify-content-end">
                <div className="mb-3">
                  <button className="btn btn-primary" onClick={() => handlePrint(props?.ticket)}>Print</button>
                </div>
              </div>
            </TabContent>
          </div>
        </Modal>

        <Modal size="md" isOpen={ratingModel} toggle={() => { tog_rating_view(); }}>
          <div className="modal-header">
            <h5 className="modal-title mt-0" id="myLargeModalLabel">Ratings And Reviews</h5>
            <button onClick={() => { tog_rating_view(); }} type="button" className="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <div className="col-md-12">
              <div className="mb-3">
                <Label className="form-Label">Was the issue resolved to your satisfaction?</Label>
                <br />
                <div className="d-flex gap-3">
                  <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" name="inlineRadioOptions" id="inlineRadio1" value={checkboxes.yes}
                      onChange={(e) => { setCheckboxes((prev) => ({ ...prev, no: !e.target.checked, yes: e.target.checked })); }}
                    />
                    <label class="form-check-label" for="inlineRadio1">Yes</label>
                  </div>
                  <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" name="inlineRadioOptions" id="inlineRadio2" value={checkboxes.no}
                      onChange={(e) => { setCheckboxes((prev) => ({ ...prev, yes: !e.target.checked, no: e.target.checked })); }}
                    />
                    <label class="form-check-label" for="inlineRadio2">No</label>
                  </div>
                </div>
              </div>
            </div>
            {checkboxes.yes && (
              <div className="col-md-12">
                <div className="mb-3">
                  <Label className="form-Label">Ratings</Label>
                  <br />
                  <Rating onClick={(rate) => { setRating(rate); }} />
                </div>
              </div>
            )}
            <div className="col-md-12">
              <div className="mb-3">
                <Label className="form-Label">Reviews</Label>
                <textarea id="basicpill-address-input text-capitalize" className="form-control" rows="3" value={review} onChange={(e) => { setReview(e.target.value); }} placeholder=""></textarea>
              </div>
            </div>
            <br />
            <div className="col-md-12 d-flex flex-wrap align-items-center justify-content-center">
              <button className="btn btn-primary mo-mb-2 mr-10" onClick={(e) => { submitReview(e); tog_rating_view(); }}>Submit</button>
              <button className="btn btn-light" onClick={(e) => { tog_rating_view(); }}>Cancel</button>
              <br />
            </div>
          </div>
        </Modal>
      </td>

      <td className="ticket-cell-unit">
        <div className="ticket-unit-main">
          {isStaff && props.propertySignature ? <span title={props.property || ""}>{props.propertySignature} · </span> : null}
          Suite {props.suite || "—"}
        </div>
        {props.permission ? <div className="ticket-unit-meta">Permission: {props.permission}</div> : null}
      </td>

      <td className="ticket-cell-age">
        <span className={`ticket-age ticket-age--${age.tone}`} title={age.title}>{age.label}</span>
      </td>

      {isStaff ? (
        <>
          <td>
            <OverlayTrigger
              placement="left"
              delay={{ show: 400, hide: 200 }}
              overlay={
                <Tooltip id={`status-hover-${props.id}`}>
                  <StatusHoverContent status={props.status} hoverData={statusHoverData} />
                </Tooltip>
              }
              onToggle={(show) => { if (show) fetchStatusHoverData(); }}
            >
              <div>
                <select
                  className={`form-select form-select-sm ticket-status-select ticket-status--${String(props?.status || "").toLowerCase()}`}
                  aria-label="Change status"
                  value={props.status}
                  onChange={(event) => {
                    setWorkflowTargetStatus(event.target.value);
                    tog_workflow();
                  }}
                >
                  <option value={"Open"}>Open</option>
                  <option value={"Scheduled"}>Scheduled</option>
                  <option value={"Inprogress"}>In progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Unresolved">Unresolved</option>
                </select>
              </div>
            </OverlayTrigger>
          </td>
          <WorkflowStatusCell
            status={props.status}
            workflow={props.maintenanceWorkflow}
          />
        </>
      ) : (
        <>
          <td>
            <span
              className={`ticket-status-pill ticket-status--${String(props?.status || "").toLowerCase()}`}
            >
              {props.status === "Inprogress" ? "In progress" : props.status}
            </span>
          </td>
        </>
      )}

      {isStaff && (
        <td className="ticket-cell-assigned">
          {assigneeName ? <div className="ticket-assignee">{assigneeName}</div> : null}
          {canAssign ? (
            <button type="button" className="btn btn-outline-primary btn-sm ticket-assign-btn" onClick={() => { tog_open_view(); }}>
              {assigneeName ? "Reassign" : "Assign"}
            </button>
          ) : props.isMovedToVendorPortal ? (
            <span className="ticket-assigned-tag">Sent to vendors</span>
          ) : !assigneeName ? (
            <span className="ticket-muted">—</span>
          ) : null}
        </td>
      )}

      <td className="ticket-cell-actions">
        <div className="ticket-actions">
          {decode.role === "customer" && props.internalStatus == "Completed" ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => { tog_rating_view(); }}>Review</button>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => { tog_view(); if (isStaff) fetchConsumedItemsDetails({ _id: props.id }); }}
            >
              View
            </button>
          )}
          <UncontrolledDropdown className="ticket-more">
            <DropdownToggle tag="button" type="button" className="btn btn-light btn-sm ticket-more-toggle" aria-label="More actions">
              <i className="bx bx-dots-horizontal-rounded" />
              {unreadNotes ? <span className="ticket-more-dot" aria-hidden="true" /> : null}
            </DropdownToggle>
            {/* Portalled to <body> so the table's scroll box never clips it. */}
            <DropdownMenu right container="body" className="ticket-more-menu">
              <DropdownItem disabled={!hasDocuments} onClick={downloadDocuments}>
                <i className="bx bx-download me-2" />
                {hasDocuments ? "Download photos" : "No photos"}
              </DropdownItem>
              {isStaff && (
                <DropdownItem onClick={() => { setShowLogs((prev) => ({ ...prev, data: props?.ticket?.history, value: true })); }}>
                  <i className="bx bx-history me-2" />
                  History
                </DropdownItem>
              )}
              <DropdownItem onClick={openModal}>
                <i className="bx bx-note me-2" />
                Memo
                {unreadNotes ? <Badge className="ms-2 ticket-memo-badge">{unreadNotes}</Badge> : null}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      </td>

      <Modal size="lg" isOpen={quoteModal} toggle={() => { setQuoteModal(!quoteModal); }}>
        <div className="modal-header">
          <h5 className="modal-title mt-0" id="myLargeModalLabel">Quotes</h5>
          <button onClick={() => { setQuoteModal(!quoteModal); }} type="button" className="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="modal-body" style={{ overflowX: "scroll" }}>
          <Table className="table-striped table-bordered mb-0">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Vendor Id</th>
                <th scope="col">Name</th>
                <th scope="col">Agency Name</th>
                <th scope="col">Estimated Amount</th>
                <th scope="col">Start Date & Time</th>
                <th scope="col">End Date & Time</th>
                <th scope="col">Notes</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            {props?.quotedVendorTickets.map((item, i) => {
              return (
                <tbody key={i}>
                  <tr>
                    <td>{i + 1}</td>
                    <td>{item?.vendorID?.primaryID}</td>
                    <td>{`${item?.vendorID?.first_name} ${item?.vendorID?.last_name}`}</td>
                    <td>{item?.vendorID?.agency_name}</td>
                    <td>{item?.estimatedAmount}</td>
                    <td>{`${moment(item?.startDate).format("L")} ${item.startTime}`}</td>
                    <td>{`${moment(item?.estimatedEndDate).format("L")} ${item.estimatedETime}`}</td>
                    <td>{item?.notes}</td>
                    <td>
                      <button
                        className="btn btn-outline-primary"
                        disabled={props?.ticket?.assignSpecificVendors?.length > 0 ? true : false}
                        onClick={() => { handleAssign(item?.ticketID, item?.vendorID?._id); }}
                      >
                        Assign
                      </button>
                    </td>
                  </tr>
                </tbody>
              );
            })}
          </Table>
        </div>
      </Modal>

      <Modal size="lg" isOpen={model_open} toggle={() => { tog_open_view(); }}>
        <div className="modal-header">
          <h5 className="modal-title mt-0" id="myLargeModalLabel">Assign</h5>
          <button onClick={() => { tog_open_view(); }} type="button" className="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="modal-body">
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "15px", padding: "5px" }}>
            <div style={{ marginRight: "15px" }} class="form-check">
              <input class="form-check-input" type="radio" name="flexRadioDefault" id="flexRadioDefault1" checked={moveTo == "all vendors"} onClick={() => { setchecked(true); setMoveTo("all vendors"); }} />
              <label class="form-check-label" for="flexRadioDefault1">Assign to All Vendors</label>
            </div>
            <div style={{ marginRight: "10px" }} class="form-check">
              <input class="form-check-input" type="radio" name="flexRadioDefault" id="flexRadioDefault2" checked={moveTo == "specific vendors"} onClick={() => { setchecked(false); setMoveTo("specific vendors"); }} />
              <label class="form-check-label" for="flexRadioDefault2">Assign to Specified Vendors</label>
            </div>
            <div style={{ marginRight: "10px" }} class="form-check">
              <input class="form-check-input" type="radio" name="flexRadioDefault" id="flexRadioDefault3" checked={moveTo == "specific technical staff"} onClick={() => { setMoveTo("specific technical staff"); }} />
              <label class="form-check-label" for="flexRadioDefault3">Assign to Maintenance Team</label>
            </div>
          </div>

          {moveTo == "specific vendors" && (
            <>
              <Card>
                <CardBody>
                  <div className="filter-sec">
                    <Form>
                      <div class="row align-items-center">
                        <div className="col-md-4">
                          <div className="mb-3">
                            <Label className="form-Label">Names</Label>
                            <Select
                              isMulti
                              onChange={(selectedOption) => { const names = []; selectedOption.map((property) => { names.push(property.value); }); setSelectedName(names); }}
                              value={selectedName.map((item) => { return { label: item, value: item }; })}
                              options={vendor.map((item) => { return { label: item.first_name, value: item.first_name }; })}
                              className="basic-multi-select"
                              classNamePrefix="select"
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <Label className="form-Label">Category</Label>
                            <Select
                              isMulti
                              value={selectedCategories.map((item) => { return { label: item, value: item }; })}
                              onChange={(selectedOption) => { const category = []; selectedOption.map((property) => { category.push(property.value); }); setSelectedCategories(category); }}
                              options={specialties.map((item) => { return { label: item, value: item }; })}
                              className="basic-multi-select"
                              classNamePrefix="select"
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <Label className="form-Label">Contact No</Label>
                            <Select
                              isMulti
                              value={selectedContact.map((item) => { return { label: item, value: item }; })}
                              onChange={(selectedOption) => { const contact_no = []; selectedOption.map((property) => { contact_no.push(property.value); }); setSelectedContact(contact_no); }}
                              options={vendor.map((item) => { return { label: item.contact_no, value: item.contact_no }; })}
                              className="basic-multi-select"
                              classNamePrefix="select"
                            />
                          </div>
                        </div>
                        <div className="col-md-2 text-end">
                          <button type="submit" className="btn btn-primary" onClick={(e) => { e.preventDefault(); specificVendorSearch(); }}>Search</button>
                        </div>
                        <div className="col-md-1">
                          <button type="submit" className="btn btn-light" onClick={(e) => { e.preventDefault(); clear(); }}>Clear</button>
                        </div>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
              <div style={{ marginTop: "15px", display: "flex", alignItems: "center" }} className="table-responsive">
                <Table className="table-striped table-bordered mb-0">
                  <thead>
                    <tr>
                      <th></th>
                      <th scope="col">#1</th>
                      <th scope="col">Category</th>
                      <th scope="col">Name</th>
                      <th scope="col">Address</th>
                      <th scope="col">Agency Name</th>
                      <th scope="col">Contact</th>
                    </tr>
                  </thead>
                  {vendor.map((item, i) => {
                    return (
                      <tbody key={i}>
                        <tr>
                          <div style={{ marginLeft: "15px" }} class="form-check">
                            <input class="form-check-input" type="radio" name="flexTableRadioDefault" onClick={() => { setTableCheck(true); setTableContent(item._id); }} />
                          </div>
                          <td>{i + 1}</td>
                          <td>{item.specialties.length > 0 ? item.specialties.map((a) => a.specialty).join() : "No Category found"}</td>
                          <td>{item.first_name}</td>
                          <td>{item.address}</td>
                          <td>{item.agency_name}</td>
                          <td>{item.contact_no}</td>
                        </tr>
                      </tbody>
                    );
                  })}
                </Table>
              </div>
            </>
          )}

          {moveTo == "specific technical staff" && (
            <Card>
              <CardBody>
                <div className="filter-sec">
                  <Form>
                    <div class="row align-items-center">
                      <div className="col-md-4">
                        <div className="mb-3">
                          <Label className="form-Label">Name</Label>
                          <Select
                            isMulti
                            onChange={(selectedOption) => { const names = []; selectedOption.map((property) => { names.push(property.value); }); setSelectedName(names); }}
                            value={selectedName.map((item) => { return { label: item, value: item }; })}
                            options={originalStaff.map((item) => { return { label: item.first_name, value: item.first_name }; })}
                            className="basic-multi-select"
                            classNamePrefix="select"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3">
                          <Label className="form-Label">Email</Label>
                          <Select
                            isMulti
                            value={selectedEmail.map((item) => { return { label: item, value: item }; })}
                            onChange={(selectedOption) => { const emails = []; selectedOption.map((property) => { emails.push(property.value); }); setSelectedEmail(emails); }}
                            options={originalStaff.map((item) => { return { label: item.email, value: item.email }; })}
                            className="basic-multi-select"
                            classNamePrefix="select"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3">
                          <Label className="form-Label">Contact No</Label>
                          <Select
                            isMulti
                            value={selectedContact.map((item) => { return { label: item, value: item }; })}
                            onChange={(selectedOption) => { const contact_no = []; selectedOption.map((property) => { contact_no.push(property.value); }); setSelectedContact(contact_no); }}
                            options={originalStaff.map((item) => { return { label: item.contact_no, value: item.contact_no }; })}
                            className="basic-multi-select"
                            classNamePrefix="select"
                          />
                        </div>
                      </div>
                      <div className="col-md-2 text-end">
                        <button type="submit" className="btn btn-primary" onClick={(e) => { e.preventDefault(); technicalStaffSearch(); }}>Search</button>
                      </div>
                      <div className="col-md-1">
                        <button type="submit" className="btn btn-light" onClick={(e) => { e.preventDefault(); clear(); }}>Clear</button>
                      </div>
                    </div>
                  </Form>
                </div>
              </CardBody>
            </Card>
          )}

          {moveTo == "specific technical staff" && (
            <div style={{ marginTop: "15px", display: "flex", alignItems: "center" }} className="table-responsive">
              <Table className="table-striped table-bordered mb-0">
                <thead>
                  <tr>
                    <th></th>
                    <th scope="col">#</th>
                    <th scope="col">Category</th>
                    <th scope="col">Name</th>
                    <th scope="col">Address</th>
                    <th scope="col">Contact</th>
                    <th scope="col">Rating</th>
                  </tr>
                </thead>
                {technicalStaff.map((item, i) => {
                  return (
                    <tbody key={i}>
                      <tr>
                        <div style={{ marginLeft: "15px" }} class="form-check">
                          <input class="form-check-input" type="radio" name="flexTableRadioDefault" onClick={() => { setTableCheck(true); setTableContent(item._id); }} />
                        </div>
                        <td>{i + 1}</td>
                        <td>{item.specialties.length > 0 ? item.specialties.map((a) => a.speciality).join() : "No Category found"}</td>
                        <td>{item.first_name}</td>
                        <td>{item.email}</td>
                        <td>{item.contact_no}</td>
                        <td><Rating ratingValue={item.rating * 20} readonly={true} size={20} /></td>
                      </tr>
                    </tbody>
                  );
                })}
              </Table>
            </div>
          )}
        </div>
        {moveTo == "specific vendors" || moveTo == "specific technical staff" ? (
          <div style={{ display: "flex", justifyContent: "center", marginTop: "15px", padding: "5px" }}>
            <button onClick={() => { moveToSpecificVendor(props.id, tableContent); setModel_open(!model_open); }} style={{ marginRight: "10px" }} className="btn btn-primary">
              {loading && <div id="saveSpinner" style={{ height: "15px", width: "15px", marginLeft: "5px" }} class="spinner-border" role="status"></div>}
              Submit
            </button>
            <button className="btn btn-light" onClick={(e) => { tog_open_view(); }}>Cancel</button>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "center", marginTop: "15px", padding: "5px" }}>
            {moveTo == "all vendors" && (
              <button style={{ marginRight: "10px" }} onClick={() => { moveToAllVendor(props.id); setModel_open(!model_open); }} className="btn btn-primary">
                {loading && <div id="saveSpinner" style={{ height: "15px", width: "15px", marginLeft: "5px" }} class="spinner-border" role="status"></div>}
                Submit
              </button>
            )}
            <button className="btn btn-light" onClick={(e) => { tog_open_view(); }}>Cancel</button>
          </div>
        )}
      </Modal>

      <Modal size="lg" isOpen={showlogs?.value} toggle={() => { setShowLogs((prev) => ({ ...prev, data: [], value: false })); }}>
        <div className="modal-header">
          <h5 className="modal-title mt-0" id="myLargeModalLabel">History Log</h5>
          <button onClick={() => { setShowLogs((prev) => ({ ...prev, data: [], value: false })); }} type="button" className="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="modal-body">
          <div className="d-flex flex-column ml-2">
            {showlogs?.data?.map((log, i) => (
              <div key={log?._id} className="d-flex mb-1">
                <div className="d-flex flex-column pr-4 align-items-center">
                  <div className="rounded-circle py-2 px-3 bg-primary text-white mb-1">{i + 1}</div>
                  <div className={`${i + 1 !== showlogs?.data?.length ? "line h-100" : ""}`}></div>
                </div>
                <div>
                  <p className="lead pb-3">
                    <span className="text-black mx-2"><strong>{log?.userName} ({log.actionBy})</strong></span>
                    {log?.action}.{" "}
                    <small className="text-muted mx-2">{moment(log?.createdAt).calendar()}</small>
                  </p>
                </div>
              </div>
            ))}
          </div>
          <TicketHistoryTimeline ticketId={props.id || ""} />
        </div>
      </Modal>

      <Modal size="md" isOpen={showModal} toggle={closeModal}>
        <div className="modal-header">
          <h5 className="modal-title mt-0" id="myLargeModalLabel">Notes</h5>
          <button onClick={closeModal} type="button" className="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="modal-body">
          <div className="d-flex flex-column ml-2" style={{ overflow: "auto", maxHeight: "70vh" }}>
            <Card className="mb-2 w-100 w-fit-content border-0">
              <CardBody className="p-0 border-0">
                {showNotes?.map((log, i) => (
                  <div key={log?._id} className="chatBox mb-2 pt-1">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="">{log?.userName}</h5>
                      <div>
                        <div className="rounded-pill px-1 bg-primary text-white text-center">{log?.user}</div>
                        <small className="text-muted mx-2" style={{ fontStyle: "italic" }}>{moment(log?.createdAt).calendar()}</small>
                      </div>
                    </div>
                    <div className="">
                      <p className="lead mb-0 p-0">
                        <span className="text-black mx-2"><strong>{log?.note}</strong></span>
                      </p>
                    </div>
                  </div>
                ))}
              </CardBody>
            </Card>
          </div>
          <div className="d-flex flex ml-2">
            <div className="divider"></div>
            <input className="w-100 me-2 px-2" onChange={(event) => { setNotesInput(event.target.value); }} value={notesInput} type="text" />
            <button onClick={() => sendNotes(props.id)} className="btn btn-primary btn-m ml-15">Send</button>
          </div>
        </div>
      </Modal>

      <Modal size="lg" isOpen={showNote} toggle={() => setShowNote(!showNotes)}>
        <div className="modal-header">
          <h5 className="modal-title mt-0" id="myLargeModalLabel">Notes</h5>
          <button onClick={() => setShowNote(!showNote)} type="button" className="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="modal-body">
          <div className="d-flex flex-column ml-2">
            {showlogs?.data?.map((log, i) => (
              <div key={log?._id} className="d-flex mb-1">
                <div className="d-flex flex-column pr-4 align-items-center">
                  <div className="rounded-circle py-2 px-3 bg-primary text-white mb-1">{i + 1}</div>
                  <div className={`${i + 1 !== showlogs?.data?.length ? "line h-100" : ""}`}></div>
                </div>
                <div>
                  <p className="lead pb-3">
                    <span className="text-black mx-2"><strong>{log?.userName} ({log.actionBy})</strong></span>
                    {log?.action}.{" "}
                    <small className="text-muted mx-2">{moment(log?.createdAt).calendar()}</small>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* MaintenanceFlowDetailsForm — replaces the old Confirm_model for all status changes */}
      <MaintenanceFlowDetailsForm
        item={{
          _id: props.id,
          propertyID: props.ticket?.propertyID,
          building: props.building,
          requestType: props.requestType,
          primaryID: props.primaryID,
          status: props.status,
        }}
        isOpen={modal_workflow}
        onClose={() => {
          tog_workflow();
          props.searchTicket();
        }}
        getTechnicalStaffTickets={props.searchTicket}
        getTotalTickets={() => { }}
        targetStatus={workflowTargetStatus}
      />
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────
// ImagesDisplay — unchanged from original
// ─────────────────────────────────────────────────────────────
const ImagesDisplay = ({ documents }) => {
  const [signedUrls, setSignedUrls] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSignedUrls = async () => {
      const prefix = "https://rdclive.nyc3.digitaloceanspaces.com/my%20spaces/";
      try {
        const documentNames = documents.map((doc) => doc.replace(prefix, ""));
        const documentNewNames = documents.map((doc, i) => `Ticket_Image_${i + 1}`);
        setLoading(true);
        const response = await axios.post("s3-services/download-images", { paths: documentNames, filenames: documentNewNames });
        const signedUrls = response.data.signedUrls;
        const validUrls = signedUrls.filter(({ error }) => !error).map(({ signedUrl }) => signedUrl);
        setSignedUrls(validUrls);
      } catch (error) {
        console.error("Error fetching signed URLs:", error);
      } finally {
        setLoading(false);
      }
    };
    if (documents.length > 0) {
      fetchSignedUrls();
    }
  }, [documents]);

  const handleDownload = (url, fileName) => {
    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <>
      {loading ? (
        <div>Loading...</div>
      ) : (
        documents.length > 0 && (
          <div className="col-md-12">
            <div className="mb-3">
              <label htmlFor="basicpill-address-input" className="form-label">Images</label>
              <div className="container">
                <div className="row">
                  {signedUrls.map((url, index) => (
                    <div className="col-12 col-md-4 mb-4" key={index}>
                      <div className="card">
                        <img src={url} className="card-img-top" alt={`Image ${index + 1}`} style={{ minHeight: 200, maxHeight: 200 }} />
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center">
                            <p className="card-text">Image {index + 1}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
};